import React from "react";
import Footer from "./footer";
import { useLanguage } from "../LanguageContext";
import { FileText } from "lucide-react";
import jsPDF from "jspdf";
import userManualPDF from "../assets/usermanual/usermanual_omc.pdf";
import { sub } from "framer-motion/client";


const ProductHMI = () => {
  const { language } = useLanguage();

  const text = {
    en: {
      title: "HMI Operation Maintenance Center",
      subtitle: "",
      subtitle:
        "This HMI dashboard serves as the visual command center for the Operation Maintenance Center (OMC), displaying the real-time status and performance monitoring of all installed field devices.",
      download: "Download the brochure",
      specTitle: "Specification",
      features: [
        { icon: require("../assets/products/hmi/bell.png"), label: "SOE Alarm, Load Trend" },
        { icon: require("../assets/products/hmi/file.png"), label: "Disturbance File" },
        { icon: require("../assets/products/hmi/map.png"), label: "UPT, ULTG, GI Level" },
        { icon: require("../assets/products/hmi/remote-control.png"), label: "Local/Remote Command" },
      ],
      specs: [
        {
          title: "System Overview",
          desc: [
            "This HMI dashboard serves as the visual center for the Operation Maintenance Center (OMC), displaying real-time status and performance monitoring of all installed field devices. The system is designed to provide a comprehensive view that helps operators oversee overall operational conditions efficiently.",
          ],
        },

        {
          title: "Main Features",
          desc: [
            "Provides real-time status of field devices",
            "Monitors operational performance of devices",
            "Offers data visualization for quick analysis",
            "Displays notifications when potential issues are detected",
          ],

        },
      ],
      documentation: "Documentation",
      docs: [
        { title: "ULTG Yogyakarta", desc: "Server Installation" },
        { title: "ULTG Ngimbang", desc: "Server Installation" },
      ],
    },

    id: {
      title: "HMI Operation Maintenance Center",

      subtitle:
        "Dashboard HMI yang berfungsi sebagai pusat visual untuk Operation Maintenance Center (OMC), menampilkan status dan pemantauan kinerja secara real-time dari seluruh perangkat lapangan yang terpasang.",
      download: "Unduh Brosur",
      specTitle: "Spesifikasi",
      features: [
        { icon: require("../assets/products/hmi/bell.png"), label: "Alarm SOE, Tren Beban" },
        { icon: require("../assets/products/hmi/file.png"), label: "File Gangguan" },
        { icon: require("../assets/products/hmi/map.png"), label: "Level UPT, ULTG, GI" },
        { icon: require("../assets/products/hmi/remote-control.png"), label: "Perintah Lokal/Jarak Jauh" },
      ],
      specs: [
        {
          title: "Gambaran Sistem",
          desc: [
            "Dashboard HMI ini berfungsi sebagai pusat visual untuk Operation Maintenance Center (OMC), menampilkan status serta pemantauan kinerja secara real-time dari seluruh perangkat lapangan yang terpasang. Sistem ini dirancang untuk memberikan tampilan komprehensif yang memudahkan operator dalam mengawasi kondisi operasional secara menyeluruh.",
          ],
        },
        {
          title: "Fitur Utama",
          desc: [
            "Menyajikan status perangkat lapangan secara real-time",
            "Memantau kinerja operasional perangkat",
            "Menyediakan visualisasi data untuk analisis cepat",
            "Menampilkan notifikasi ketika ada indikasi gangguan",
          ],
        },
      ],
      documentation: "Dokumentasi",
      docs: [
        { title: "ULTG Yogyakarta", desc: "Pemasangan Server" },
        { title: "ULTG Ngimbang", desc: "Pemasangan Server" },
      ],
    },
  };


  const t = text[language] || text.en;


  const handleDownloadManual = async () => {
    try {
      const response = await fetch(userManualPDF);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;


      a.download = "mel_usermanual_hmiomc.pdf";

      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };



  return (
    <div className="font-inter text-gray-900 px-4 sm:px-8 md:px-20 lg:px-40">
      <section
        className="relative flex flex-col md:flex-row-reverse items-center justify-between 
             px-4 sm:px-6 md:px-10 py-10 sm:py-14 md:py-20 
             mx-4 sm:mx-6 md:mx-10 
             mt-20 sm:mt-14 md:mt-20 
             overflow-hidden rounded-[40px] 
             shadow-[0_25px_60px_rgba(55,140,231,0.5)]"
        style={{
          background:
            "linear-gradient(100deg, #DFF5FF 10%, #FFFFFF 40%, #2652A4 100%)",
        }}
      >
        <div className="w-full md:w-1/2 mb-6 md:mb-0 flex justify-center z-10">
          <img
            src={require("../assets/products/hmi/hmi.png")}
            alt={t.title}
            className="w-full max-w-[700px] sm:max-w-[800px] object-contain drop-shadow-[0_20px_50px_rgba(55,140,231,0.6)] transition-transform duration-500 ease-out"
          />
        </div>

        <div className="w-full md:w-1/2 z-20 text-left text-[#1446A5] md:pr-10 sm:md:pr-20">
          <h1 className="text-2xl sm:text-3xl md:text-3xl font-extrabold mb-4 sm:mb-6 leading-snug">
            {t.title} <br />
            <span className="text-[#1446A5] drop-shadow-md">HMI OMC</span>
          </h1>

          <h2 className="text-lg sm:text-xl md:text-lg opacity-100 font-medium mb-4 sm:mb-6 leading-relaxed max-w-[400px] sm:max-w-[480px]">
            {t.subtitle}
          </h2>

          <button
            onClick={handleDownloadManual}
            className="bg-[#2652A4] text-white font-medium text-sm px-3 py-2 rounded-full shadow-md hover:shadow-lg flex items-center gap-1 transition-all hover:scale-105"
          >
            {t.download}
            <FileText className="w-4 h-5" />
          </button>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            {t.features.map((f, i) => (
              <div key={i} className="flex flex-col items-center">
                <img
                  src={f.icon}
                  alt={f.label}
                  className="w-8 sm:w-10 md:w-12 mb- "
                />
                <p className="text-[#1446A5] text-xs sm:text-sm font-medium leading-snug">
                  {f.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 md:px-10 py-8 sm:py-12 md:py-18 mt-20">
        <div className="block md:hidden mb-6">
          <h3 className="text-[#1446A5] text-2xl sm:text-3xl font-extrabold leading-loose tracking-widest">
            {t.specTitle}
          </h3>
        </div>

        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:gap-12 items-start">
          <div className="order-2 md:order-1 md:w-1/2 flex justify-center md:justify-start">
            <h3 className="text-[#1446A5] hidden md:block text-3xl sm:text-4xl md:text-6xl font-extrabold leading-loose tracking-widest md:mt-32 ml-0">
              {t.specTitle}
            </h3>
          </div>

          <div className="order-1 md:order-2 md:w-1/2 flex flex-col justify-start h-full">
            <div className="grid grid-rows-2">
              {t.specs.map((spec, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl p-6 flex flex-col h-auto"
                >
                  <div>
                    <h4 className="font-semibold mb-3 text-lg sm:text-xl text-[#1446A5]">
                      {spec.title}
                    </h4>

                    {Array.isArray(spec.desc) ? (
                      spec.title === "Fitur Utama" || spec.title === "Main Features" ? (
                        <ul className="mt-1 text-gray-700 space-y-1 list-disc ml-4">
                          {spec.desc.map((d, idx) => (
                            <li key={idx} className="text-sm sm:text-base leading-relaxed">
                              {d}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="mt-1 text-gray-700 space-y-1">
                          {spec.desc.map((d, idx) => (
                            <p key={idx} className="text-sm sm:text-base leading-relaxed">
                              {d}
                            </p>
                          ))}
                        </div>
                      )
                    ) : (
                      <p className="mt-1 text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                        {spec.desc}
                      </p>
                    )}

                  </div>
                  <div className="mt-4 border-b border-gray-300 w-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};


export default ProductHMI;
